import * as React from 'react';
import { Line } from 'react-chartjs-2';
import {
    Chart,
    TimeScale,
    LinearScale,
    PointElement,
    LineElement,
    LineController,
    Filler,
    Tooltip,
} from 'chart.js';
import type { ChartData, ChartOptions } from 'chart.js';
import 'chartjs-adapter-date-fns';
import { MetricPoint } from './PodChartsStore';

Chart.register(TimeScale, LinearScale, PointElement, LineElement, LineController, Filler, Tooltip);

export type MetricKind = 'cpu' | 'memory';

export interface IMetricChartProps {
    readonly points: readonly MetricPoint[];
    readonly kind: MetricKind;
    /** Allocatable CPU (millicores) or memory (bytes) — the declared limit. It becomes the TOP of the
     *  y-axis, so the plot reads directly as "how much of the allowance is in use" and every pod sharing
     *  a limit is drawn to one scale. Null when nothing is declared, and the axis then auto-scales.  */
    readonly capacity: number | null;
    /** Left edge of the window, so a series shorter than the window doesn't stretch to fill it. */
    readonly from: Date;
    readonly to: Date;
}

const COLORS: Record<MetricKind, { line: string; fill: string }> = {
    cpu: { line: 'rgb(13, 110, 253)', fill: 'rgba(13, 110, 253, 0.15)' },
    memory: { line: 'rgb(111, 66, 193)', fill: 'rgba(111, 66, 193, 0.15)' },
};

/** Drops trailing zeros so 2.00 reads "2" and 0.500 reads "0.5". */
function trimZeros(text: string): string {
    return text.includes('.') ? text.replace(/0+$/, '').replace(/\.$/, '') : text;
}

/**
 * Millicores -> a bare cores figure: 2000 -> "2", 500 -> "0.5", 47 -> "0.047".
 *
 * The cluster reports CPU in millicores ("m", a thousandth of a core) but that unit means nothing to most
 * readers, so it is converted to cores everywhere it is shown. Precision grows as the number shrinks —
 * an idle pod is a few thousandths of a core and would otherwise round away to "0".
 */
export function formatCores(milliCores: number): string {
    const cores = milliCores / 1000;
    if (cores === 0) {
        return '0';
    }
    const decimals = cores >= 1 ? 2 : cores >= 0.01 ? 3 : 4;
    return trimZeros(cores.toFixed(decimals));
}

/** Millicores -> "2 cores" / "0.5 cores" / "1 core". */
export function formatCoreCapacity(milliCores: number): string {
    const cores = formatCores(milliCores);
    return `${cores} ${cores === '1' ? 'core' : 'cores'}`;
}

/** Bytes -> "512 MiB" / "6 GiB". */
export function formatMemory(bytes: number): string {
    const mib = bytes / (1024 * 1024);
    if (mib < 1024) {
        return `${Math.round(mib)} MiB`;
    }
    return `${trimZeros((mib / 1024).toFixed(2))} GiB`;
}

/**
 * Percent -> "33%". A non-zero reading below 1% shows as "<1%" rather than rounding to "0%": an idle pod
 * against a 2-core limit really is a fraction of a percent, and "0%" reads as "not running".
 */
export function formatPercent(percent: number): string {
    if (percent > 0 && percent < 1) {
        return '<1%';
    }
    return `${percent.toFixed(0)}%`;
}

/** The absolute reading, with its unit — used where there is no limit to express it as a percentage. */
export function formatMetric(kind: MetricKind, value: number): string {
    return kind === 'cpu' ? formatCoreCapacity(value) : formatMemory(value);
}

export class MetricChart extends React.Component<IMetricChartProps> {
    public render() {
        const { points, kind, from, to, capacity } = this.props;
        const color = COLORS[kind];

        // A declared limit is the axis ceiling. The one exception is a reading that exceeds it: clamping
        // there would draw a flat line along the top and hide the overshoot, which is the single most
        // important thing on the chart. CPU is throttled at its limit and memory is OOM-killed past it,
        // so this should be rare — but when it happens it must be visible, not clipped away.
        const peak = points.reduce((highest, point) => Math.max(highest, point[kind]), 0);
        const yMax = capacity != null && capacity > 0 ? Math.max(capacity, peak) : undefined;

        const data: ChartData<'line'> = {
            datasets: [
                {
                    data: points.map(p => ({ x: new Date(p.t).getTime(), y: p[kind] })) as any,
                    borderColor: color.line,
                    backgroundColor: color.fill,
                    borderWidth: 1.5,
                    // A week of points makes markers a smear; the tooltip still resolves the nearest one.
                    pointRadius: 0,
                    pointHitRadius: 8,
                    tension: 0.25,
                    fill: true,
                },
            ],
        };

        const options: ChartOptions<'line'> = {
            responsive: true,
            maintainAspectRatio: false,
            animation: false,
            interaction: { mode: 'index', intersect: false },
            scales: {
                x: {
                    type: 'time',
                    min: from.getTime(),
                    max: to.getTime(),
                    time: { displayFormats: { minute: 'HH:mm', hour: 'HH:mm', day: 'MMM d' } },
                    ticks: { maxRotation: 0, autoSkip: true, maxTicksLimit: 6, font: { size: 10 } },
                    grid: { color: 'rgba(128, 128, 128, 0.15)' },
                },
                y: {
                    beginAtZero: true,
                    max: yMax,
                    ticks: {
                        maxTicksLimit: 4,
                        font: { size: 10 },
                        // Bare numbers on the CPU axis — the unit is stated once on the card label, so
                        // repeating "cores" on every tick just crowds a 150px-tall chart.
                        callback: (value: any) =>
                            kind === 'cpu' ? formatCores(Number(value)) : formatMemory(Number(value)),
                    },
                    grid: { color: 'rgba(128, 128, 128, 0.15)' },
                },
            },
            plugins: {
                legend: { display: false },
                tooltip: {
                    callbacks: {
                        // The tooltip also shows the raw millicore reading, so anyone comparing this page
                        // against `kubectl top` (which prints millicores) can line the two up.
                        label: (item: any) =>
                            kind === 'cpu'
                                ? `${formatCoreCapacity(item.parsed.y)} (${Math.round(item.parsed.y)}m)`
                                : formatMemory(item.parsed.y),
                    },
                },
            },
        };

        return (
            <div style={{ height: '150px' }}>
                <Line data={data} options={options} />
            </div>
        );
    }
}
